import { useState, useEffect, useRef } from 'react';
import {
  Play, Loader2, History, ChevronDown, Timer, Database, Table2, Download, Search, ArrowUpDown
} from 'lucide-react';
import { dataEngineApi } from '../../services/dataEngineApi';
import StatusBadge from './StatusBadge';

/* ─── Helpers ──────────────────────────────────────────── */
const formatDate = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    const now = new Date();
    const diffH = (now - date) / 3600000;
    if (diffH < 1) return `${Math.round(diffH * 60)}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    if (diffH < 168) return `${Math.round(diffH / 24)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

const formatFullDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const formatDuration = (ms) => {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/* ─── Step Timings View ───────────────────────────────── */
const PHASE_COLORS = {
  PLAN:     { bg: 'bg-sky-500',    bgFaint: 'bg-sky-500/10',    border: 'border-sky-500/20',    text: 'text-sky-400'     },
  EXECUTE:  { bg: 'bg-blue-500',   bgFaint: 'bg-blue-500/10',   border: 'border-blue-500/20',   text: 'text-blue-400'    },
  FINALIZE: { bg: 'bg-violet-500', bgFaint: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400'  },
  VALIDATE: { bg: 'bg-amber-500',  bgFaint: 'bg-amber-500/10',  border: 'border-amber-500/20',  text: 'text-amber-400'   },
};

const STEP_LABELS = {
  evaluate_sync_plan: 'Evaluate Plan',
  generate_sync_token: 'Generate Token',
  prepare_schema_and_accounts: 'Prepare Schema',
  fetch_gl_data: 'Fetch GL Data',
  parse_gl_data: 'Parse GL Data',
  store_transactions: 'Store Transactions',
  verify_period: 'Verify Period',
  promote_staging: 'Promote Staging',
  swap_sync_token: 'Swap Token',
  fetch_pnl_summary: 'Fetch P&L Summary',
  aggregate_transactions: 'Aggregate Txns',
  validate_comparison: 'Validate',
  finalize_sync: 'Finalize',
};

function StepTimingsView({ steps, summary }) {
  const [expandedStep, setExpandedStep] = useState(null);
  const maxMs = Math.max(...steps.map(s => s.duration_ms || 0), 1);
  const totalApiCalls = steps.reduce((sum, s) => sum + (s.details?.api_calls || 0), 0);

  return (
    <div className="bg-gray-800/20 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <Timer className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Pipeline Steps</span>
        </div>
        <span className="text-[11px] text-gray-500">
          {summary && <>Total {formatDuration(summary.total_ms)}</>}
          {totalApiCalls > 0 && <> · {totalApiCalls} QB API calls</>}
          {summary?.slowest_step && (
            <> · Slowest: <span className="text-gray-400">{STEP_LABELS[summary.slowest_step.step] || summary.slowest_step.step}</span> ({formatDuration(summary.slowest_step.duration_ms)})</>
          )}
        </span>
      </div>

      {summary?.phases && (
        <div className="flex gap-2 px-3 py-2 border-b border-gray-800/50">
          {Object.entries(summary.phases).map(([phase, info]) => {
            const colors = PHASE_COLORS[phase] || PHASE_COLORS.PLAN;
            return (
              <div key={phase} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium ${colors.bgFaint} border ${colors.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${colors.bg}`} />
                <span className={colors.text}>{phase}</span>
                <span className="text-gray-500">{formatDuration(info.duration_ms)}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="divide-y divide-gray-800/30">
        {steps.map((step, i) => {
          const colors = PHASE_COLORS[step.phase] || PHASE_COLORS.PLAN;
          const pct = Math.max(((step.duration_ms || 0) / maxMs) * 100, 1);
          const details = step.details || {};
          const perPeriod = details.per_period;
          const hasExpand = perPeriod && perPeriod.length > 0;
          const isExpanded = expandedStep === i;

          const summaryParts = [];
          if (details.api_calls) summaryParts.push(`${details.api_calls} calls`);
          if (details.total_rows) summaryParts.push(`${details.total_rows.toLocaleString()} rows`);
          if (details.slowest_call_ms) summaryParts.push(`slowest: ${formatDuration(details.slowest_call_ms)}`);
          if (details.avg_call_ms) summaryParts.push(`avg: ${formatDuration(details.avg_call_ms)}`);
          if (details.inserted) summaryParts.push(`${details.inserted.toLocaleString()} inserted`);
          if (details.method) summaryParts.push(details.method);
          if (details.report) summaryParts.push(details.report);
          if (details.categories) summaryParts.push(`${details.categories} categories`);
          if (details.period_count) summaryParts.push(`${details.period_count} periods`);
          if (details.granularity) summaryParts.push(details.granularity);
          if (details.accounts_loaded) summaryParts.push(`${details.accounts_loaded} accounts`);
          if (typeof details.matches_expected === 'boolean') summaryParts.push(details.matches_expected ? 'verified' : 'mismatch');

          return (
            <div key={i}>
              <div
                className={`flex items-center gap-3 px-3 py-1.5 group hover:bg-gray-800/20 transition-colors ${hasExpand ? 'cursor-pointer' : ''}`}
                onClick={hasExpand ? () => setExpandedStep(isExpanded ? null : i) : undefined}
              >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.bg}`} />
                <span className="text-[11px] text-gray-400 w-32 flex-shrink-0 truncate" title={step.step}>
                  {STEP_LABELS[step.step] || step.step.replace(/_/g, ' ')}
                </span>
                <div className="flex-1 h-4 bg-gray-800/40 rounded overflow-hidden relative">
                  <div className={`h-full rounded ${colors.bg} opacity-30 transition-all`} style={{ width: `${pct}%` }} />
                  {summaryParts.length > 0 && (
                    <div className="absolute inset-0 flex items-center px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[9px] text-gray-400 truncate">{summaryParts.join(' · ')}</span>
                    </div>
                  )}
                </div>
                <span className={`text-[11px] font-mono w-14 text-right flex-shrink-0 ${
                  step.duration_ms > 30000 ? 'text-amber-400' : step.duration_ms > 5000 ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  {formatDuration(step.duration_ms)}
                </span>
                {hasExpand && (
                  <ChevronDown className={`h-3 w-3 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                )}
              </div>

              {isExpanded && perPeriod && (
                <div className="bg-gray-800/10 px-3 py-2 ml-8 mr-3 mb-1 rounded-lg">
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 text-[10px]">
                    <span className="text-gray-600 uppercase tracking-wider">Period</span>
                    <span className="text-gray-600 uppercase tracking-wider text-right">Rows</span>
                    <span className="text-gray-600 uppercase tracking-wider text-right">Duration</span>
                    <span className="text-gray-600 uppercase tracking-wider text-right">Status</span>
                    {perPeriod.map((pp, j) => {
                      const ppPct = Math.max(((pp.duration_ms || 0) / (details.slowest_call_ms || 1)) * 100, 2);
                      return (
                        <div key={j} className="contents">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-800/40 rounded overflow-hidden">
                              <div className={`h-full rounded ${colors.bg} opacity-40`} style={{ width: `${ppPct}%` }} />
                            </div>
                            <span className="text-gray-500 font-mono whitespace-nowrap">{pp.start} → {pp.end}</span>
                          </div>
                          <span className="text-gray-400 text-right font-mono">{(pp.rows || 0).toLocaleString()}</span>
                          <span className={`text-right font-mono ${
                            pp.duration_ms > 10000 ? 'text-amber-400' : 'text-gray-500'
                          }`}>{formatDuration(pp.duration_ms)}</span>
                          <span className="text-right">{pp.truncated ? <span className="text-amber-400">truncated</span> : <span className="text-emerald-500/60">ok</span>}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── GL Sync Tab ─────────────────────────────────────── */
export default function GLSyncTab({ company, onSyncComplete }) {
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // current poll status
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const pollRef = useRef(null);

  // Fetch job history on mount
  useEffect(() => {
    setJobsLoading(true);
    dataEngineApi.getPipelineJobs(0, 5, null, { rfId: company.rf_id })
      .then(data => setJobs(data.data || []))
      .catch(() => setJobs([]))
      .finally(() => setJobsLoading(false));
  }, [company.rf_id]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const result = await dataEngineApi.triggerSync(company.rf_id);
      if (result.data?.job_id) {
        const jobId = result.data.job_id;
        pollRef.current = setInterval(async () => {
          try {
            const log = await dataEngineApi.getPipelineLog(jobId);
            const s = log.data?.status;
            setSyncStatus(log.data);
            if (['COMPLETED', 'COMPLETED_WITH_WARNINGS', 'FAILED'].includes(s)) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setSyncing(false);
              // Refresh job history
              dataEngineApi.getPipelineJobs(0, 5, null, { rfId: company.rf_id })
                .then(data => setJobs(data.data || []))
                .catch(() => {});
              if (onSyncComplete) onSyncComplete();
            }
          } catch {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setSyncing(false);
          }
        }, 3000);
      }
    } catch {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* GL Status */}
      <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-300">GL Sync Status</h3>
          <button
            onClick={handleSync}
            disabled={syncing}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-all ${
              syncing
                ? 'bg-blue-500/10 text-blue-400 cursor-wait'
                : 'text-blue-400 bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20'
            }`}
          >
            {syncing
              ? <><Loader2 className="h-3 w-3 animate-spin" /> Syncing...</>
              : <><Play className="h-3 w-3" /> Sync GL Data</>}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">Records</div>
            <div className="text-lg font-semibold text-gray-200 font-mono">
              {company.row_count > 0 ? company.row_count.toLocaleString() : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">File Size</div>
            <div className="text-lg font-semibold text-gray-200 font-mono">
              {formatSize(company.file_size_bytes)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">Last Synced</div>
            <div className="text-lg font-semibold text-gray-200">
              {formatDate(company.last_synced_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Live sync progress */}
      {syncStatus && syncStatus.step_timings && syncStatus.step_timings.length > 0 && (
        <StepTimingsView steps={syncStatus.step_timings} summary={syncStatus.timing_summary} />
      )}

      {/* Job History */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <History className="h-4 w-4 text-gray-500" />
          Recent Sync History
        </h3>

        {jobsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-600">
            <Database className="h-6 w-6 mb-2 opacity-40" />
            <p className="text-xs">No sync runs yet</p>
          </div>
        ) : (
          <div className="bg-gray-800/30 border border-gray-800 rounded-xl overflow-hidden divide-y divide-gray-800">
            {jobs.map(job => (
              <div key={job.job_id}>
                <button
                  onClick={() => setExpandedJob(expandedJob === job.job_id ? null : job.job_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-800/50 transition-colors"
                >
                  <StatusBadge status={job.status} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-gray-300">{formatFullDate(job.started_at)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {job.execute_summary?.total_inserted > 0 && (
                      <span>{job.execute_summary.total_inserted.toLocaleString()} rows</span>
                    )}
                    <span>{formatDuration(job.duration_ms)}</span>
                  </div>
                  <ChevronDown className={`h-3.5 w-3.5 text-gray-600 transition-transform ${expandedJob === job.job_id ? 'rotate-180' : ''}`} />
                </button>

                {expandedJob === job.job_id && (
                  <div className="px-4 pb-4 pt-1 space-y-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs bg-gray-800/30 rounded-lg p-3">
                      <div className="flex justify-between"><span className="text-gray-500">Job ID</span><span className="text-gray-400 font-mono text-[10px]">{job.job_id.slice(0, 8)}...</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="text-gray-400">{job.pipeline_type}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Started</span><span className="text-gray-400">{formatFullDate(job.started_at)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Completed</span><span className="text-gray-400">{formatFullDate(job.completed_at)}</span></div>
                    </div>

                    {job.execute_summary && (
                      <div className="flex gap-3 text-xs">
                        <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg px-3 py-2 flex-1">
                          <div className="text-blue-400 font-medium">{(job.execute_summary.total_inserted || 0).toLocaleString()}</div>
                          <div className="text-gray-500 mt-0.5">Records inserted</div>
                        </div>
                        {job.execute_summary.periods_processed && (
                          <div className="bg-purple-500/5 border border-purple-500/10 rounded-lg px-3 py-2 flex-1">
                            <div className="text-purple-400 font-medium">{job.execute_summary.periods_processed}</div>
                            <div className="text-gray-500 mt-0.5">Periods processed</div>
                          </div>
                        )}
                        <div className="bg-gray-500/5 border border-gray-600/10 rounded-lg px-3 py-2 flex-1">
                          <div className="text-gray-300 font-medium">{formatDuration(job.duration_ms)}</div>
                          <div className="text-gray-500 mt-0.5">Duration</div>
                        </div>
                      </div>
                    )}

                    {job.step_timings && job.step_timings.length > 0 && (
                      <StepTimingsView steps={job.step_timings} summary={job.timing_summary} />
                    )}

                    {job.error_message && (
                      <div className="bg-red-500/5 border border-red-500/10 rounded-lg px-3 py-2 text-xs text-red-400">
                        {job.error_message.length > 200 ? job.error_message.slice(0, 200) + '...' : job.error_message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* GL Data Viewer */}
      <GLDataViewer rfId={company.rf_id} companyName={company.display_name} />
    </div>
  );
}

/* ─── GL Data Viewer ───────────────────────────────────── */
const DATA_ENGINE_URL = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
const GL_PAGE_SIZE = 100;

function GLDataViewer({ rfId }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');

  const loadData = () => {
    if (data) { setExpanded(e => !e); return; }
    setExpanded(true);
    setLoading(true);
    fetch(`${DATA_ENGINE_URL}/api/query/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rf_ids: [rfId],
        sql: 'SELECT transaction_date, transaction_type, document_number, account_name, account_type, cust_name, vend_name, memo, amount, balance FROM qb_transactions ORDER BY transaction_date DESC',
        max_rows: 5000,
      }),
    })
      .then(r => r.json())
      .then(d => setData(d.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  const toggleSort = (idx) => {
    if (sortCol === idx) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(idx); setSortDir('asc'); }
  };

  const rows = data?.rows || [];
  const columns = data?.columns || [];

  const filtered = search
    ? rows.filter(r => r.some(c => String(c ?? '').toLowerCase().includes(search.toLowerCase())))
    : rows;

  const sorted = sortCol !== null
    ? [...filtered].sort((a, b) => {
        const va = a[sortCol], vb = b[sortCol];
        const cmp = typeof va === 'number' ? va - vb : String(va ?? '').localeCompare(String(vb ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const paged = sorted.slice(page * GL_PAGE_SIZE, (page + 1) * GL_PAGE_SIZE);
  const totalPages = Math.ceil(sorted.length / GL_PAGE_SIZE);

  const exportCSV = () => {
    const header = columns.join(',');
    const body = sorted.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${rfId}-gl-transactions.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const formatCell = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'number' || (typeof val === 'string' && /^-?\d+\.\d{4}$/.test(val))) {
      const n = parseFloat(val);
      if (!isNaN(n)) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(val);
  };

  return (
    <div className="bg-gray-800/30 border border-gray-800 rounded-xl overflow-hidden">
      <button
        onClick={loadData}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-800/30 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Table2 className="h-4 w-4 text-purple-400" />
          GL Transaction Data
          {data && <span className="text-[10px] text-gray-600 font-normal">{rows.length.toLocaleString()} records</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-gray-800">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
            </div>
          ) : !data || rows.length === 0 ? (
            <div className="text-center py-8 text-xs text-gray-600">No GL data. Sync first.</div>
          ) : (
            <>
              {/* Controls */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800/50">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                  <input
                    type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search transactions..."
                    className="w-full bg-gray-800/60 border border-gray-700/50 rounded pl-8 pr-3 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-gray-600"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-600">{sorted.length.toLocaleString()} rows</span>
                  <button onClick={exportCSV} className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded transition-all">
                    <Download className="h-3 w-3" /> CSV
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-900">
                    <tr className="border-b border-gray-800">
                      {columns.map((col, i) => (
                        <th
                          key={col}
                          onClick={() => toggleSort(i)}
                          className="px-3 py-2 text-left text-[10px] text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-300 whitespace-nowrap"
                        >
                          <span className="flex items-center gap-1">
                            {col.replace(/_/g, ' ')}
                            {sortCol === i && <ArrowUpDown className="h-2.5 w-2.5" />}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((row, ri) => (
                      <tr key={ri} className="border-b border-gray-800/30 hover:bg-gray-800/20">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-1.5 text-gray-400 whitespace-nowrap font-mono">
                            {formatCell(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-gray-800/50">
                  <span className="text-[10px] text-gray-600">Page {page + 1} of {totalPages}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPage(0)} disabled={page === 0}
                      className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded disabled:opacity-30">First</button>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded disabled:opacity-30">Prev</button>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                      className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded disabled:opacity-30">Next</button>
                    <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                      className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded disabled:opacity-30">Last</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
